import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  Accordion,
  Badge,
  Button,
  Card,
  CardColors,
  Icon,
  Link,
  Popup,
  Progress,
  Text,
} from '@/ui'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { faPencil, faArrowRight } from '@fortawesome/pro-light-svg-icons'
import {
  faQuestionCircle,
  faBullseye,
} from '@fortawesome/pro-regular-svg-icons'
import { useAnalyticsTrack } from '@/features/amplitude'
import { InsightsTrendGraph } from '@/features/financial-insights/practice-insights/InsightsTrendGraph'
import { fetchInsightsBarchartData } from '@/features/financial-insights/shared/financeActions'
import {
  INSIGHTS_TYPE,
  processDataForChart,
} from '@/features/financial-insights/shared/utils'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  selectExpenseRatioGoalDifference,
  selectIncomeGoalPercentage,
  selectProfitGoal,
  selectProfitGoalPercentage,
  selectYtdExpenseRatio,
  selectYtdExpenses,
  selectYtdIncome,
  selectYtdProfit,
} from '@/features/goal-setting/goals.selectors'
import { TextColors, TypographyStyles } from '@/styles'

export const YearToDateProfitLossCard = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const [trendData, setTrendData] = useState<
    { [key: string]: string | number | undefined }[]
  >([])
  const track = useAnalyticsTrack()
  const todayDate = useMemo(() => DateTime.now(), [])

  const profitGoalPercentage = useReselector(selectProfitGoalPercentage)
  const ytdIncome = useReselector(selectYtdIncome)
  const ytdExpenses = useReselector(selectYtdExpenses)
  const profitGoal = useReselector(selectProfitGoal)
  const incomeGoalPercentage = useReselector(selectIncomeGoalPercentage)
  const ytdExpenseRatio = useReselector(selectYtdExpenseRatio)
  const expenseRatioDiff = useReselector(selectExpenseRatioGoalDifference)
  const ytdProfit = useReselector(selectYtdProfit)

  useEffect(() => {
    const fetchTrendData = async () => {
      const startDate = todayDate
        .startOf('year')
        .toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      const endDate = todayDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      const endOfYear = todayDate
        .endOf('year')
        .toFormat(DATE_FORMATS_LUXON.TIMESTAMP)

      const fetchedData = await fetchInsightsBarchartData(
        INSIGHTS_TYPE.PROFIT,
        startDate,
        endDate
      )(dispatch)

      if (fetchedData) {
        const chartData = processDataForChart(
          fetchedData,
          [startDate, endOfYear],
          INSIGHTS_TYPE.PROFIT
        )
        setTrendData(chartData)
      }
    }
    fetchTrendData()
  }, [dispatch, todayDate])

  return (
    <Card>
      <div className="flex flex-col sm:flex-row">
        <div className="sm:w-5/8">
          <Text variant={TypographyStyles.H1} className="header-text">
            Year to Date Profit & Loss
          </Text>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {todayDate
              .startOf('year')
              .toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}{' '}
            - {todayDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}
          </Text>
        </div>
        <div className="mt-2 sm:mt-0 sm:w-3/8 flex gap-4 sm:justify-end items-start [&_button]:px-0">
          <Button
            variant="link"
            onClick={() => {
              navigate('/goal-setting')
            }}
            iconLeft={faPencil}
          >
            {fp?.annualIncomeGoalInCents || fp?.annualExpenseRatioGoal
              ? 'Edit Goals'
              : 'Set Goals'}
          </Button>
          <Button
            variant="link"
            onClick={() => {
              track('Clicked year to date profiles View Reports Button')
              navigate('/insights/overview')
            }}
            iconRight={faArrowRight}
          >
            View Reports
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4 mt-2">
        <Card color={CardColors.LEVEL_1} className="my-1 md:my-4">
          <Text variant={TypographyStyles.DISPLAY_MD} color={TextColors.HEADER}>
            {formatCurrency(ytdIncome)}
          </Text>
          <Text
            variant={TypographyStyles.LABEL_SM}
            color={TextColors.MUTED}
            className="flex items-center"
          >
            <span className="truncate">Income (YTD)</span>
            <Popup
              tooltipTriggerClassName="align-middle"
              trigger={
                <Icon
                  icon={faQuestionCircle}
                  className="ml-1 shrink-0"
                  color={TextColors.MUTED}
                  style={{ fontSize: 16 }}
                />
              }
              content={
                <>
                  <Text variant={TypographyStyles.LABEL_MD}>
                    Year-to-Date Income
                  </Text>
                  <br />
                  <Text variant={TypographyStyles.BODY_SM}>
                    This is the total income that has been categorized by
                    Heard&apos;s bookkeeping team this calendar year.
                    <br />
                    <br />
                    If you&apos;ve set a goal, the bar graph will track your
                    progress towards your income target that you set.
                    <br />
                    <br />
                    Use the &quot;Edit Targets&quot; button in the upper-right
                    hand corner of Year to Date Profit and Loss to update your
                    target.
                  </Text>
                </>
              }
            />
          </Text>
          <br />
          {fp?.annualIncomeGoalInCents ? (
            <div>
              <div className="flex gap-2 items-center mb-3">
                <Progress value={incomeGoalPercentage} />
                <Text
                  color={TextColors.HIGHLIGHT}
                  variant={TypographyStyles.LABEL_SM}
                >
                  {incomeGoalPercentage}%
                </Text>
              </div>
              <Text className="truncate">
                Annual Income Goal of{' '}
                {formatCurrencyFromCents(fp.annualIncomeGoalInCents, {
                  hideDecimalsIfZero: true,
                })}
              </Text>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate('/goal-setting')}
              className="w-full"
            >
              <Icon icon={faBullseye} />
              Set Goal
            </Button>
          )}
          <br />
          <Link
            href="/insights/income"
            trackMessage="Clicked year to date income"
            iconRight={faArrowRight}
            className="mt-4 max-w-full"
          >
            <span className="truncate">View breakdown</span>
          </Link>
        </Card>
        <Card color={CardColors.LEVEL_1} className="my-1 md:my-4">
          <Text variant={TypographyStyles.DISPLAY_MD} color={TextColors.HEADER}>
            -{formatCurrency(Math.abs(ytdExpenses))}
          </Text>
          <Text
            variant={TypographyStyles.LABEL_SM}
            color={TextColors.MUTED}
            className="flex items-center"
          >
            <span className="truncate">Expenses (YTD)</span>
            <Popup
              tooltipTriggerClassName="align-middle"
              trigger={
                <Icon
                  icon={faQuestionCircle}
                  className="ml-1 shrink-0"
                  color={TextColors.MUTED}
                  style={{ fontSize: 16 }}
                />
              }
              content={
                <>
                  <Text variant={TypographyStyles.LABEL_MD}>
                    Year-to-Date Expenses
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                    This is the total amount of expenses that has been
                    categorized by Heard&apos;s bookkeeping team this calendar
                    year.
                    <br />
                    If you&apos;ve set a goal, you will see your expense ratio
                    (your expenses as a percentage of your income) for the year
                    compared with your target.
                    <br />
                    Use the &quot;Edit Targets&quot; button in the upper-right
                    hand corner of Year to Date Profit and Loss to update your
                    target.
                  </Text>
                </>
              }
            />
          </Text>
          <br />
          {fp?.annualExpenseRatioGoal ? (
            <div>
              <div className="flex gap-2 mb-2">
                <Text variant={TypographyStyles.DISPLAY_SM}>
                  {ytdExpenseRatio}%
                </Text>
                {Boolean(expenseRatioDiff) && (
                  <Badge
                    color={expenseRatioDiff > 10 ? 'orange' : 'gray'}
                    variant="secondary"
                  >
                    {expenseRatioDiff > 0 && '+'}
                    {expenseRatioDiff.toFixed(2)}%
                  </Badge>
                )}
              </div>
              <Text variant={TypographyStyles.BODY_XS} className="truncate">
                Expense Ratio Goal of {fp.annualExpenseRatioGoal * 100}%
              </Text>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate('/goal-setting')}
              className="w-full"
            >
              <Icon icon={faBullseye} />
              Set Goal
            </Button>
          )}
          <br />
          <Link
            href="/insights/expenses"
            trackMessage="Clicked year to date expenses"
            iconRight={faArrowRight}
            className="mt-4 max-w-full"
          >
            <span className="truncate">View breakdown</span>
          </Link>
        </Card>

        <Card color={CardColors.LEVEL_1} className="my-1 md:my-4">
          <Text variant={TypographyStyles.DISPLAY_MD} color={TextColors.HEADER}>
            {formatCurrency(ytdProfit, { hideDecimalsIfZero: true })}
          </Text>
          <Text
            variant={TypographyStyles.LABEL_SM}
            color={TextColors.MUTED}
            className="flex items-center"
          >
            <span className="truncate">Profit (YTD)</span>
            <Popup
              tooltipTriggerClassName="align-middle"
              trigger={
                <Icon
                  icon={faQuestionCircle}
                  className="ml-1 shrink-0"
                  color={TextColors.MUTED}
                  style={{ fontSize: 16 }}
                />
              }
              content={
                <>
                  <Text variant={TypographyStyles.LABEL_MD}>
                    Year-to-Date Profit
                  </Text>
                  <br />
                  <Text variant={TypographyStyles.BODY_SM}>
                    This is the total bookkeeper reconciled amount after
                    expenses are paid, or your take-home earnings before taxes
                    for this calendar year.
                    <br />
                    <br />
                    If you&apos;ve set a goal, you will see your profit for the
                    year compared with your target.
                    <br />
                    <br />
                    Use the &quot;Edit Targets&quot; button in the upper-right
                    hand corner of Year to Date Profit and Loss to update your
                    target.
                  </Text>
                </>
              }
            />
          </Text>
          <br />
          {fp?.annualIncomeGoalInCents && fp.annualExpenseRatioGoal ? (
            <div>
              <div className="flex gap-2 items-center mb-3">
                <Progress value={profitGoalPercentage} />
                <Text
                  color={TextColors.HIGHLIGHT}
                  variant={TypographyStyles.LABEL_SM}
                >
                  {profitGoalPercentage}%
                </Text>
              </div>
              <Text className="truncate">
                Annual Profit Goal of{' '}
                {formatCurrency(profitGoal, { hideDecimalsIfZero: true })}
              </Text>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate('/goal-setting')}
              className="w-full"
            >
              <Icon icon={faBullseye} />
              Set Goal
            </Button>
          )}
          <br />
          <Link
            href="/insights/profit"
            trackMessage="Clicked year to date profit"
            iconRight={faArrowRight}
            className="mt-4 max-w-full"
          >
            <span className="truncate">View breakdown</span>
          </Link>
        </Card>
      </div>
      <Accordion type="single" collapsible>
        <Accordion.Item value="chart" className="border-none">
          <Accordion.Trigger className="justify-start gap-2">
            Year-to-Date Profit and Loss Chart
          </Accordion.Trigger>
          <Accordion.Content>
            <InsightsTrendGraph data={trendData} />
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  )
}
